"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

interface WorkflowWizardContextType {
  isWizardMode: boolean;
  currentStep: number; // 1-6 (Client, Quote, Job, Schedule, Complete, Invoice)
  startWizard: () => void;
  exitWizard: () => void;
  goToNextStep: () => void;
  goToStep: (step: number) => void;
  lastCreatedClientId: string | null;
  lastCreatedQuoteId: string | null;
  lastCreatedJobId: string | null;
  setLastCreatedClientId: (id: string) => void;
  setLastCreatedQuoteId: (id: string) => void;
  setLastCreatedJobId: (id: string) => void;
}

interface WorkflowWizardState {
  isWizardMode: boolean;
  currentStep: number;
  lastCreatedClientId: string | null;
  lastCreatedQuoteId: string | null;
  lastCreatedJobId: string | null;
}

const WorkflowWizardContext = createContext<WorkflowWizardContextType | undefined>(
  undefined
);

const STORAGE_KEY = "workflow-wizard-state";
const TOTAL_STEPS = 6;

const getInitialState = (): WorkflowWizardState => {
  if (typeof window === "undefined") {
    return {
      isWizardMode: false,
      currentStep: 1,
      lastCreatedClientId: null,
      lastCreatedQuoteId: null,
      lastCreatedJobId: null,
    };
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Failed to parse workflow wizard state from sessionStorage:", error);
  }

  return {
    isWizardMode: false,
    currentStep: 1,
    lastCreatedClientId: null,
    lastCreatedQuoteId: null,
    lastCreatedJobId: null,
  };
};

export function WorkflowWizardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WorkflowWizardState>(getInitialState);

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error("Failed to save workflow wizard state to sessionStorage:", error);
      }
    }
  }, [state]);

  const startWizard = () => {
    setState({
      isWizardMode: true,
      currentStep: 1,
      lastCreatedClientId: null,
      lastCreatedQuoteId: null,
      lastCreatedJobId: null,
    });
  };

  const exitWizard = () => {
    setState({
      isWizardMode: false,
      currentStep: 1,
      lastCreatedClientId: null,
      lastCreatedQuoteId: null,
      lastCreatedJobId: null,
    });
  };

  const goToNextStep = () => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, TOTAL_STEPS),
    }));
  };

  const goToStep = (step: number) => {
    if (step < 1 || step > TOTAL_STEPS) {
      console.warn(`Invalid step: ${step}. Must be between 1 and ${TOTAL_STEPS}.`);
      return;
    }
    setState((prev) => ({
      ...prev,
      currentStep: step,
    }));
  };

  const setLastCreatedClientId = (id: string) => {
    setState((prev) => ({
      ...prev,
      lastCreatedClientId: id,
    }));
  };

  const setLastCreatedQuoteId = (id: string) => {
    setState((prev) => ({
      ...prev,
      lastCreatedQuoteId: id,
    }));
  };

  const setLastCreatedJobId = (id: string) => {
    setState((prev) => ({
      ...prev,
      lastCreatedJobId: id,
    }));
  };

  const value: WorkflowWizardContextType = {
    isWizardMode: state.isWizardMode,
    currentStep: state.currentStep,
    startWizard,
    exitWizard,
    goToNextStep,
    goToStep,
    lastCreatedClientId: state.lastCreatedClientId,
    lastCreatedQuoteId: state.lastCreatedQuoteId,
    lastCreatedJobId: state.lastCreatedJobId,
    setLastCreatedClientId,
    setLastCreatedQuoteId,
    setLastCreatedJobId,
  };

  return (
    <WorkflowWizardContext.Provider value={value}>
      {children}
    </WorkflowWizardContext.Provider>
  );
}

export function useWorkflowWizard(): WorkflowWizardContextType {
  const context = useContext(WorkflowWizardContext);
  if (context === undefined) {
    throw new Error("useWorkflowWizard must be used within a WorkflowWizardProvider");
  }
  return context;
}
